import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { mockList } from '@/lib/mock/db';
import { queryKeys } from '@/lib/queryKeys';
import { TREND_TYPES } from '@/lib/types';
import { relativeDay } from '@/pages/home/widgets/format';
import { WidgetFrame, WidgetRow } from '@/pages/home/widgets/WidgetFrame';
import { relativeTime, useNewsItems, useNewsSections } from '@/pages/news/useNews';
import { safeHref } from '@/lib/utils/url';

/**
 * 뉴스·건설동향. 반칸 위젯이고, <b>토글로 둘을 가른다.</b>
 *
 * <h2>왜 한 칸인가 (사용자 지시 2026-09-18)</h2>
 * 같은 날 두 메뉴가 사이드바에서 내려갔다 — 들어가는 문은 이제 이 위젯의 <b>전체보기</b> 하나다.
 * 그런데 건설동향 위젯은 기본이 꺼짐이라, 따로 두면 그것을 켠 적 없는 사람에게는 문이 아예
 * 사라진다. 기본으로 켜져 있는 이쪽으로 들여, <b>입구가 없어지는 사람이 없게</b> 했다.
 *
 * <p>홈에 비슷한 네모가 둘 서는 것도 함께 없어졌다 — 나란히 두면 둘 다 대충 읽힌다
 * (착공·준공을 「현장 준비」 하나로 합친 것과 같은 판단).
 *
 * <h2>뉴스 쪽은 섹션을 골라 볼 수 있다</h2>
 * 전용 화면의 탭을 그대로 축소해 놓은 것이다 — 홈에서 "경제만 잠깐" 보고 싶은 흐름이 실제로 있고,
 * 그때마다 전체보기로 넘어가야 하면 위젯을 둔 의미가 없다.
 *
 * 고른 섹션은 <b>주소에 남기지 않는다</b>(전용 화면은 남긴다). 홈은 위젯이 여럿인 대시보드라
 * 위젯 하나의 상태를 URL 에 실으면 다른 위젯과 자리를 다투고, 홈 주소를 공유하는 일도 없다.
 *
 * 제목을 누르면 언론사 원문으로 나간다 — 전용 화면과 같은 규칙이다.
 */

/** 반칸에 부담 없이 들어가는 줄 수. 더 보려면 전체보기로 넘어간다. */
const VISIBLE_COUNT = 6;

/** 건설동향은 줄이 길어(제목 + 종류 배지) 넷이면 반칸이 찬다. */
const TREND_VISIBLE_COUNT = 4;

const TREND_TYPE_LABELS = new Map(TREND_TYPES.map((item) => [item.value, item.label] as const));

/** 섹션을 고르지 않은 상태 — 전 섹션을 섞어 보여 준다. */
const ALL = 'all';

type Scope = 'news' | 'trends';

export function NewsWidget() {
  const [scope, setScope] = useState<Scope>('news');
  const [selected, setSelected] = useState<string>(ALL);

  const sections = useNewsSections();
  const sectionId = selected === ALL ? null : selected;
  const items = useNewsItems(sectionId, VISIBLE_COUNT);
  const trends = useTrendPosts();

  const isNews = scope === 'news';

  return (
    <WidgetFrame
      title={isNews ? '뉴스' : '건설동향'}
      icon="news"
      // 전체보기가 <b>유일한 입구</b>다(2026-09-18). 보고 있는 쪽으로 간다.
      to={isNews ? '/news/headlines' : '/news/trends'}
      // 「바뀐 것」 안내가 이 링크를 반짝인다(`lib/tour/whatsNew`) — 메뉴가 내려간 지금
      // <b>여기가 유일한 입구</b>라, 위젯 칸만 밝히면 어디를 눌러야 하는지 말해 주지 못한다.
      toTourKey="widget-news-more"
      pending={isNews ? sections.isPending || items.isPending : trends.isPending}
      error={isNews ? items.error : trends.error}
      onRetry={isNews ? items.refetch : trends.refetch}
      empty={(isNews ? items.data?.length : trends.data?.length) === 0}
      emptyText={isNews ? '아직 수집된 뉴스 기사가 없습니다.' : '등록된 건설동향 소식이 없습니다.'}
      headerRight={<ScopeToggle scope={scope} onChange={setScope} />}
    >
      {isNews ? (
        <>
          {sections.data && sections.data.length > 0 && (
            // 섹션이 열 개를 넘으므로 줄바꿈 대신 가로로 민다. 반칸에서 두 줄 세 줄로 늘어나면
            // 정작 기사가 밀려난다.
            <div className="flex gap-1 overflow-x-auto mb-2 pb-1" style={{ scrollbarWidth: 'thin' }}>
              <SectionChip label="전체" active={selected === ALL} onClick={() => setSelected(ALL)} />
              {sections.data.map((section) => (
                <SectionChip
                  key={section.id}
                  label={section.label}
                  active={selected === section.id}
                  onClick={() => setSelected(section.id)}
                />
              ))}
            </div>
          )}

          <div className="space-y-0.5">
            {items.data?.map((item) => (
              <WidgetRow
                key={item.id}
                href={safeHref(item.link)}
                title={item.title}
                meta={relativeTime(item.publishedAt)}
                leading={
                  item.sourceName ? (
                    // 반칸이라 가로가 좁다. 언론사 이름이 길면 배지가 제목을 밀어내므로 여기서 자른다 —
                    // 제목이 줄어드는 것보다 출처가 줄어드는 편이 낫다(제목이 이 목록의 본문이다).
                    <span
                      className="badge badge-gray badge-sm flex-shrink-0 truncate"
                      style={{ maxWidth: '64px' }}
                    >
                      {item.sourceName}
                    </span>
                  ) : undefined
                }
              />
            ))}
          </div>
        </>
      ) : (
        <div className="space-y-0.5">
          {trends.data?.map((post) => (
            <WidgetRow
              key={post.id}
              to="/news/trends"
              leading={
                <span className="badge badge-gray flex-shrink-0 badge-sm">
                  {TREND_TYPE_LABELS.get(post.trendType) ?? post.trendType}
                </span>
              }
              title={post.title}
              meta={relativeDay(post.createdAt)}
            />
          ))}
        </div>
      )}
    </WidgetFrame>
  );
}

/**
 * 뉴스 / 건설동향. 「주단위 식단표」의 본사·현장 토글과 <b>같은 모양</b>이다 — 홈에서 한 위젯이
 * 두 자료를 가를 때 쓰는 생김새를 하나로 둔다.
 */
function ScopeToggle({ scope, onChange }: { scope: Scope; onChange: (next: Scope) => void }) {
  return (
    <span className="inline-flex rounded-lg overflow-hidden" style={{ border: '1px solid var(--border)' }}>
      {(['news', 'trends'] as const).map((value) => (
        <button
          key={value}
          onClick={() => onChange(value)}
          aria-pressed={scope === value}
          className="text-ui-xs"
          style={{
            padding: '3px 9px',
            whiteSpace: 'nowrap',
            fontWeight: scope === value ? 700 : 400,
            background: scope === value ? 'var(--accent-bg)' : 'white',
            color: scope === value ? 'var(--accent)' : 'var(--text-muted)',
          }}
        >
          {value === 'news' ? '뉴스' : '건설동향'}
        </button>
      ))}
    </span>
  );
}

/**
 * 건설동향 목록.
 *
 * <p>초안 개념은 없다 — 수집기가 가져온 글도 바로 보인다. 부적절한 것은 전용 화면에서 삭제 권한을
 * 가진 사람이 지우고, 지운 기사는 다시 수집되지 않는다.
 */
function useTrendPosts() {
  return useQuery({
    queryKey: queryKeys.constructionTrendPosts,
    queryFn: () => mockList('constructionTrendPosts'),
    select: (posts) =>
      posts
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        .slice(0, TREND_VISIBLE_COUNT),
  });
}

function SectionChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex-shrink-0 rounded-full text-ui-xs"
      style={{
        padding: '3px 9px',
        fontWeight: active ? 700 : 500,
        whiteSpace: 'nowrap',
        border: 'none',
        cursor: 'pointer',
        color: active ? 'var(--accent)' : 'var(--text-muted)',
        background: active ? 'var(--accent-bg)' : 'var(--bg-hover)',
      }}
    >
      {label}
    </button>
  );
}
